import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';

import { Button } from '../ui/Button';
import { useCreateDiscountCode } from '../../hooks/useCreateDiscountCode';
import { useLockScroll } from '../../hooks/useLockScroll';
import { useUpdateDiscountCode } from '../../hooks/useUpdateDiscountCode';
import type { DiscountCode } from '../../hooks/useDiscountCodes';
import type { Database } from '../../types/supabase';

interface CreateDiscountCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string | undefined;
  discountCode?: DiscountCode | null;
  onSuccess?: () => void;
}

const getInitialForm = (code?: DiscountCode | null) => {
  if (!code) {
    return {
      code: '',
      description: '',
      discountType: 'percent' as const,
      discountValue: '',
      duration: 'forever' as const,
      durationMonths: '',
      maxRedemptions: '',
      expiresAt: '',
    };
  }
  return {
    code: code.code,
    description: code.description ?? '',
    discountType: code.discount_type as 'percent' | 'fixed',
    discountValue: code.discount_value.toString(),
    duration: code.duration_type as 'forever' | 'repeating' | 'once',
    durationMonths: code.duration_months?.toString() ?? '',
    maxRedemptions: code.max_redemptions?.toString() ?? '',
    expiresAt: code.expires_at ? code.expires_at.slice(0, 10) : '',
  };
};

/**
 * Create/Edit Discount Code Modal - Creates or updates discount_codes for company promotions.
 */
export function CreateDiscountCodeModal({
  isOpen,
  onClose,
  companyId,
  discountCode,
  onSuccess,
}: CreateDiscountCodeModalProps) {
  const createDiscountCode = useCreateDiscountCode();
  const updateDiscountCode = useUpdateDiscountCode();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(getInitialForm(null));

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialForm(discountCode));
      setError(null);
    }
  }, [isOpen, discountCode]);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm((f) => ({ ...f, code }));
  };

  const handleClose = () => {
    setError(null);
    setForm(getInitialForm(null));
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!discountCode && !companyId) return;

    setError(null);

    if (!form.code.trim()) {
      setError('Code is required');
      return;
    }
    if (!form.discountValue) {
      setError('Discount value is required');
      return;
    }
    if (
      form.duration === 'repeating' &&
      (!form.durationMonths || parseInt(form.durationMonths, 10) <= 0)
    ) {
      setError('Number of months is required when duration is Repeating');
      return;
    }

    const basePayload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim() || null,
      discount_type: form.discountType,
      discount_value: parseFloat(form.discountValue) || 0,
      duration_type: form.duration,
      duration_months:
        form.duration === 'repeating'
          ? parseInt(form.durationMonths, 10) || null
          : null,
      max_redemptions: form.maxRedemptions
        ? parseInt(form.maxRedemptions, 10)
        : null,
      expires_at: form.expiresAt || null,
    };

    if (discountCode) {
      updateDiscountCode.mutate(
        { id: discountCode.id, ...basePayload },
        {
          onSuccess: () => {
            handleClose();
            onSuccess?.();
          },
          onError: (err: unknown) => {
            const e = err as { code?: string; message?: string };
            if (e?.code === '23505') {
              setError('This code already exists');
            } else {
              setError(e?.message || 'Failed to update discount code');
            }
          },
        },
      );
    } else {
      if (!companyId) return;
      const insertData: Database['public']['Tables']['discount_codes']['Insert'] =
        {
          company_id: companyId,
          ...basePayload,
          is_active: true,
        };
      createDiscountCode.mutate(insertData, {
        onSuccess: () => {
          handleClose();
          onSuccess?.();
        },
        onError: (err: unknown) => {
          const e = err as { code?: string; message?: string };
          if (e?.code === '23505') {
            setError('This code already exists');
          } else {
            setError(e?.message || 'Failed to create discount code');
          }
        },
      });
    }
  };

  if (!isOpen) return null;

  const isEditing = Boolean(discountCode);
  const isPending = createDiscountCode.isPending || updateDiscountCode.isPending;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-[70] transition-opacity"
        onClick={handleClose}
        aria-hidden
      />

      <div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        role="dialog"
        aria-label={isEditing ? 'Edit discount code' : 'Create discount code'}
        onClick={handleClose}
      >
        <div
          className="w-full max-w-xl max-h-[90vh] bg-[#1a1818] border border-[#504A4A] rounded-xl shadow-2xl flex flex-col animate-in zoom-in-95 fade-in-0 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#504A4A] shrink-0">
            <h2 className="text-lg font-semibold text-[#EBFFEE]">
              {isEditing ? 'Edit Discount Code' : 'Create Discount Code'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="p-2 rounded-lg text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5 transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0 flex flex-col"
          >
            {error && (
              <div className="p-3 rounded-lg bg-red-900/20 border border-red-500/50 text-sm text-red-400">
                {error}
              </div>
            )}

            {/* SECTION 1: Code */}
            <section>
              <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">Code</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
                  }
                  placeholder="e.g., WELCOME20"
                  className="flex-1 px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] font-mono uppercase placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                />
                <button
                  type="button"
                  onClick={generateCode}
                  className="px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] hover:border-[#504A4A]/80 text-[#A9A3A3] hover:text-[#EBFFEE] rounded-lg transition-colors text-sm font-medium"
                >
                  Generate
                </button>
              </div>
            </section>

            {/* SECTION 2: Description */}
            <section>
              <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                Description
              </h3>
              <input
                type="text"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="e.g., New member discount"
                className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
              />
            </section>

            {/* SECTION 3: Discount Type & Value */}
            <section>
              <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                Discount
              </h3>
              <div className="space-y-3">
                <div className="flex rounded-lg bg-[#221F1F] p-1 border border-[#504A4A]">
                  {(['percent', 'fixed'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({ ...f, discountType: type }))
                      }
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors border-2 ${
                        form.discountType === type
                          ? 'border-white bg-white/5 text-[#EBFFEE]'
                          : 'border-transparent text-[#A9A3A3] hover:text-[#EBFFEE]'
                      }`}
                    >
                      {type === 'percent'
                        ? 'Percentage (%)'
                        : 'Fixed Amount ($)'}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  {form.discountType === 'fixed' && (
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">
                      $
                    </span>
                  )}
                  <input
                    type={form.discountType === 'percent' ? 'number' : 'text'}
                    step={form.discountType === 'percent' ? '1' : '0.01'}
                    value={form.discountValue}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, discountValue: e.target.value }))
                    }
                    placeholder={
                      form.discountType === 'percent' ? '20' : '10.00'
                    }
                    min={form.discountType === 'percent' ? 0 : undefined}
                    max={form.discountType === 'percent' ? 100 : undefined}
                    className={`w-full py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700 ${
                      form.discountType === 'fixed' ? 'pl-8 pr-4' : 'px-4'
                    }`}
                  />
                  {form.discountType === 'percent' && (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">
                      %
                    </span>
                  )}
                </div>
              </div>
            </section>

            {/* SECTION 4: Duration */}
            <section>
              <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                Duration
              </h3>
              <div className="space-y-3">
                <div className="flex rounded-lg bg-[#221F1F] p-1 border border-[#504A4A]">
                  {(['forever', 'repeating', 'once'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({ ...f, duration: type }))
                      }
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors border-2 ${
                        form.duration === type
                          ? 'border-white bg-white/5 text-[#EBFFEE]'
                          : 'border-transparent text-[#A9A3A3] hover:text-[#EBFFEE]'
                      }`}
                    >
                      {type === 'forever'
                        ? 'Forever'
                        : type === 'repeating'
                          ? 'Repeating'
                          : 'Once'}
                    </button>
                  ))}
                </div>
                {form.duration === 'repeating' && (
                  <input
                    type="number"
                    value={form.durationMonths}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        durationMonths: e.target.value,
                      }))
                    }
                    placeholder="Number of months"
                    min={1}
                    className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                  />
                )}
              </div>
            </section>

            {/* SECTION 5: Optional Fields */}
            <section>
              <h3 className="text-sm font-medium text-[#A9A3A3] mb-3">
                Optional
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-[#A9A3A3] mb-2">
                    Max Redemptions
                  </label>
                  <input
                    type="number"
                    value={form.maxRedemptions}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        maxRedemptions: e.target.value,
                      }))
                    }
                    placeholder="Leave empty for unlimited"
                    min={1}
                    className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] text-sm focus:outline-none focus:border-green-700"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white mb-2">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    value={form.expiresAt}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, expiresAt: e.target.value }))
                    }
                    className="w-full px-4 py-2.5 bg-[#221F1F] border border-[#504A4A] rounded-lg text-white text-sm focus:outline-none focus:border-green-700 [color-scheme:dark]"
                  />
                </div>
              </div>
            </section>

            <div className="p-4 border-t border-[#504A4A] bg-[#1a1818] shrink-0 -mx-4 -mb-4 mt-auto">
              <Button
                type="submit"
                disabled={isPending || (isEditing ? !discountCode : !companyId)}
                className="w-full py-3 bg-green-700 hover:bg-green-800 text-white text-base font-semibold disabled:opacity-50"
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2 inline" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditing ? 'Update Discount Code' : 'Create Discount Code'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
